/**
 * Simple Error Logger for Development Only
 * 
 * Captures errors and sends them to backend to be written to frontend.log
 */

class ErrorLogger {
  private isDevelopment: boolean;
  private errorBuffer: string[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    
    if (this.isDevelopment) {
      this.setupErrorHandlers();
      this.startPeriodicFlush();
    }
  }

  private setupErrorHandlers(): void {
    // Catch unhandled errors
    window.addEventListener('error', (event) => {
      // Skip React's own error messages to avoid loops
      if (event.message?.includes('Maximum update depth exceeded')) {
        return;
      }
      
      const errorLog = `ERROR: ${new Date().toISOString()} - ${event.message} at ${event.filename}:${event.lineno}:${event.colno}\n${event.error?.stack || 'No stack trace'}\n`;
      this.errorBuffer.push(errorLog);
    });

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const errorLog = `UNHANDLED PROMISE REJECTION: ${new Date().toISOString()} - ${event.reason}\n${event.reason?.stack || 'No stack trace'}\n`;
      this.errorBuffer.push(errorLog);
    });
  }

  private startPeriodicFlush(): void {
    // Flush every 5 seconds
    this.flushTimer = setInterval(() => {
      this.flush();
    }, 5000);
  }

  private async flush(): Promise<void> {
    if (!this.isDevelopment || this.errorBuffer.length === 0) {
      return;
    }

    const errors = [...this.errorBuffer];
    this.errorBuffer = [];

    try {
      await fetch('/api/frontend-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs: errors.join('\n') }),
      });
    } catch (err) {
      // If we can't send logs, just console.error them
      console.error('Failed to send error logs:', err);
      console.error('Unsent error logs:', errors);
    }
  }

  // Log an error manually
  logError(message: string, error?: Error): void {
    if (!this.isDevelopment) return;
    
    const errorLog = `ERROR: ${new Date().toISOString()} - ${message}\n${error?.stack || 'No stack trace'}\n`;
    this.errorBuffer.push(errorLog);
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

// Ensure logs are flushed before page unload
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('beforeunload', () => {
    errorLogger.destroy();
  });
}

export default errorLogger; 